import { useTranslation } from 'react-i18next';

import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {
  Autocomplete,
  Checkbox,
  Chip,
  IconButton,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { usePromoCustomerGroups, usePromoSalesChannels } from '../../hooks';
import { defaultParamsForLeafType, isLeafValid } from '../../lib/conditionTree';
import type { ConditionTreeLeaf } from '../../lib/conditionTree';
import type { ToastSeverity } from '../../lib/useToast';
import type { ConditionLeafType } from '../../model/types';
import { ChipListInput } from './ChipListInput';
import { ProductSetPicker } from './ProductSetPicker';

const LEAF_TYPE_GROUPS: { headingKey: string; types: ConditionLeafType[] }[] = [
  {
    headingKey: 'conditionEditor.group.cart',
    types: ['MIN_SUBTOTAL', 'MIN_ITEM_QUANTITY', 'PRODUCT_IN_SET', 'CATEGORY_IN_SET'],
  },
  {
    headingKey: 'conditionEditor.group.customer',
    types: ['CUSTOMER_IN_GROUP', 'CUSTOMER_KIND', 'CUSTOMER_BIRTHDAY', 'ORDER_COUNT_AT_LEAST'],
  },
  {
    headingKey: 'conditionEditor.group.context',
    types: [
      'CHANNEL_IN',
      'STORE_IN',
      'DELIVERY_ZONE_IN',
      'SCHEDULE',
      'PAYMENT_METHOD_IN',
      'DELIVERY_METHOD_IN',
      'COUPON_PRESENT',
    ],
  },
];

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]; // ISO: Mon..Sun

export interface ConditionLeafRowProps {
  leaf: ConditionTreeLeaf;
  onChange: (leaf: ConditionTreeLeaf) => void;
  onRemove: () => void;
  disabled?: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}

export function ConditionLeafRow({ leaf, onChange, onRemove, disabled = false, onResult }: ConditionLeafRowProps) {
  const { t } = useTranslation('promotions');
  const channels = usePromoSalesChannels();
  const groups = usePromoCustomerGroups();
  const valid = isLeafValid(leaf);

  const setParam = (key: string, value: unknown) => onChange({ ...leaf, params: { ...leaf.params, [key]: value } });
  const setType = (type: ConditionLeafType) => onChange({ ...leaf, type, params: defaultParamsForLeafType(type) });

  const params = leaf.params;

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
      <TextField
        select
        size="small"
        label={t('conditionEditor.typeLabel')}
        value={leaf.type}
        disabled={disabled}
        onChange={(e) => setType(e.target.value as ConditionLeafType)}
        sx={{ minWidth: 220 }}
      >
        {LEAF_TYPE_GROUPS.flatMap((group) => [
          <ListSubheader key={group.headingKey}>{t(group.headingKey)}</ListSubheader>,
          ...group.types.map((type) => (
            <MenuItem key={type} value={type}>
              {t(`conditionEditor.leafType.${type}`)}
            </MenuItem>
          )),
        ])}
      </TextField>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flex={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        {leaf.type === 'MIN_SUBTOTAL' ? (
          <TextField
            size="small"
            type="number"
            label={t('conditionEditor.params.amount')}
            value={params.amount ?? ''}
            disabled={disabled}
            onChange={(e) => setParam('amount', Number(e.target.value))}
            helperText={t('conditionEditor.params.minSubtotalHint')}
            slotProps={{ htmlInput: { min: 0 } }}
            sx={{ width: 180 }}
          />
        ) : null}

        {leaf.type === 'MIN_ITEM_QUANTITY' ? (
          <TextField
            size="small"
            type="number"
            label={t('conditionEditor.params.quantity')}
            value={params.quantity ?? ''}
            disabled={disabled}
            onChange={(e) => setParam('quantity', Number(e.target.value))}
            slotProps={{ htmlInput: { min: 1 } }}
            sx={{ width: 140 }}
          />
        ) : null}

        {leaf.type === 'PRODUCT_IN_SET' || leaf.type === 'CATEGORY_IN_SET' ? (
          <ProductSetPicker
            value={(params.productSetId as string) ?? null}
            onChange={(id) => setParam('productSetId', id)}
            disabled={disabled}
            error={!valid}
            onResult={onResult}
          />
        ) : null}

        {leaf.type === 'CUSTOMER_IN_GROUP' ? (
          <Autocomplete
            multiple
            size="small"
            disabled={disabled}
            options={groups.data ?? []}
            loading={groups.isFetching}
            value={(groups.data ?? []).filter((g) => (params.groupCodes as string[] | undefined)?.includes(g.code))}
            getOptionLabel={(g) => g.name}
            isOptionEqualToValue={(a, b) => a.code === b.code}
            onChange={(_e, next) => setParam('groupCodes', next.map((g) => g.code))}
            renderTags={(tags, getTagProps) =>
              tags.map((tag, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip size="small" label={tag.name} key={key} {...tagProps} />;
              })
            }
            renderInput={(params2) => <TextField {...params2} label={t('conditionEditor.params.customerGroups')} />}
            sx={{ minWidth: 240 }}
          />
        ) : null}

        {leaf.type === 'CUSTOMER_KIND' ? (
          <TextField
            select
            size="small"
            label={t('conditionEditor.params.customerKind')}
            value={params.kind ?? 'REGISTERED'}
            disabled={disabled}
            onChange={(e) => setParam('kind', e.target.value)}
            sx={{ width: 200 }}
          >
            <MenuItem value="ANONYMOUS">{t('conditionEditor.customerKind.ANONYMOUS')}</MenuItem>
            <MenuItem value="REGISTERED">{t('conditionEditor.customerKind.REGISTERED')}</MenuItem>
          </TextField>
        ) : null}

        {leaf.type === 'CUSTOMER_BIRTHDAY' ? (
          <TextField
            size="small"
            type="number"
            label={t('conditionEditor.params.withinDays')}
            value={params.withinDays ?? ''}
            disabled={disabled}
            onChange={(e) => setParam('withinDays', Number(e.target.value))}
            slotProps={{ htmlInput: { min: 1 } }}
            sx={{ width: 160 }}
          />
        ) : null}

        {leaf.type === 'ORDER_COUNT_AT_LEAST' ? (
          <TextField
            size="small"
            type="number"
            label={t('conditionEditor.params.orderCount')}
            value={params.count ?? ''}
            disabled={disabled}
            onChange={(e) => setParam('count', Number(e.target.value))}
            helperText={Number(params.count) === 0 ? t('conditionEditor.params.orderCountFirstHint') : ' '}
            slotProps={{ htmlInput: { min: 0 } }}
            sx={{ width: 160 }}
          />
        ) : null}

        {leaf.type === 'CHANNEL_IN' ? (
          <Autocomplete
            multiple
            size="small"
            disabled={disabled}
            options={channels.data ?? []}
            loading={channels.isFetching}
            value={(channels.data ?? []).filter((c) => (params.channelCodes as string[] | undefined)?.includes(c.code))}
            getOptionLabel={(c) => c.name}
            isOptionEqualToValue={(a, b) => a.code === b.code}
            onChange={(_e, next) => setParam('channelCodes', next.map((c) => c.code))}
            renderTags={(tags, getTagProps) =>
              tags.map((tag, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip size="small" label={tag.name} key={key} {...tagProps} />;
              })
            }
            renderInput={(params2) => <TextField {...params2} label={t('conditionEditor.params.channels')} />}
            sx={{ minWidth: 260 }}
          />
        ) : null}

        {leaf.type === 'STORE_IN' ? (
          <ChipListInput
            label={t('conditionEditor.params.stores')}
            value={(params.storeCodes as string[]) ?? []}
            onChange={(storeCodes) => setParam('storeCodes', storeCodes)}
            helperText={t('conditionEditor.params.codesHint')}
          />
        ) : null}

        {leaf.type === 'DELIVERY_ZONE_IN' ? (
          <ChipListInput
            label={t('conditionEditor.params.zones')}
            value={(params.zoneCodes as string[]) ?? []}
            onChange={(zoneCodes) => setParam('zoneCodes', zoneCodes)}
            helperText={t('conditionEditor.params.codesHint')}
          />
        ) : null}

        {leaf.type === 'PAYMENT_METHOD_IN' ? (
          <ChipListInput
            label={t('conditionEditor.params.paymentMethods')}
            value={(params.methodCodes as string[]) ?? []}
            onChange={(methodCodes) => setParam('methodCodes', methodCodes)}
            helperText={t('conditionEditor.params.codesHint')}
          />
        ) : null}

        {leaf.type === 'DELIVERY_METHOD_IN' ? (
          <ChipListInput
            label={t('conditionEditor.params.deliveryMethods')}
            value={(params.methodCodes as string[]) ?? []}
            onChange={(methodCodes) => setParam('methodCodes', methodCodes)}
            helperText={t('conditionEditor.params.codesHint')}
          />
        ) : null}

        {leaf.type === 'SCHEDULE' ? (
          <Stack spacing={1}>
            <ToggleButtonGroup
              size="small"
              value={(params.weekdays as number[]) ?? []}
              disabled={disabled}
              onChange={(_e, next: number[]) => setParam('weekdays', next)}
              aria-label={t('conditionEditor.params.weekdays')}
            >
              {WEEKDAYS.map((day) => (
                <ToggleButton key={day} value={day} aria-label={t(`conditionEditor.weekday.${day}`)}>
                  {t(`conditionEditor.weekdayShort.${day}`)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small"
                type="time"
                label={t('conditionEditor.params.fromTime')}
                value={params.fromTime ?? '00:00'}
                disabled={disabled}
                onChange={(e) => setParam('fromTime', e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                size="small"
                type="time"
                label={t('conditionEditor.params.toTime')}
                value={params.toTime ?? '23:59'}
                disabled={disabled}
                onChange={(e) => setParam('toTime', e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Stack>
          </Stack>
        ) : null}

        {leaf.type === 'COUPON_PRESENT' ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 1 }}>
            <Checkbox checked disabled size="small" />
            <Typography variant="body2" color="text.secondary">
              {t('conditionEditor.params.couponPresentHint')}
            </Typography>
          </Stack>
        ) : null}
      </Stack>

      <Tooltip title={t('conditionEditor.removeConditionAria')}>
        <span>
          <IconButton
            size="small"
            aria-label={t('conditionEditor.removeConditionAria')}
            onClick={onRemove}
            disabled={disabled}
          >
            <DeleteOutlineRoundedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}
